import { saveAs } from 'file-saver';

export type CaptureOutcome =
  | { kind: 'blob'; blob: Blob }
  | { kind: 'failed'; message: string }
  | { kind: 'too-large'; message: string }
  | { kind: 'timeout' }
  | { kind: 'no-delivery' }
  | { kind: 'busy' }
  | { kind: 'aborted' };

export type CaptureExportOptions = {
  /** How long to wait for the blob, and then for an export the capture gave up on to settle. */
  timeoutMs: number;
  /** How long to wait for exports already running, user or captured, before ending busy. */
  settleMs: number;
  signal: AbortSignal;
};

type Capture = { settle: (outcome: CaptureOutcome) => void };

type SinkState = {
  capture: Capture | null;
  capturedRuns: Set<Capture>;
  exportsInFlight: number;
  idleWaiters: VoidFunction[];
};

type Bounds<T> = {
  timeoutMs: number;
  signal: AbortSignal;
  onTimeout: T;
  onAbort: T;
};

const state: SinkState = {
  capture: null,
  capturedRuns: new Set(),
  exportsInFlight: 0,
  idleWaiters: [],
};

const noop = (): void => undefined;

const toFailureMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return String(error);
  }
  return error.name && error.name !== 'Error'
    ? `${error.name}: ${error.message}`
    : error.message;
};

const EXPORT_TOO_LARGE_ERROR = 'ExportTooLargeError';

export const toExportTooLargeError = (message: string): Error =>
  Object.assign(Error(message), { name: EXPORT_TOO_LARGE_ERROR });

const toFailedOutcome = (error: unknown): CaptureOutcome => ({
  kind:
    error instanceof Error && error.name === EXPORT_TOO_LARGE_ERROR
      ? 'too-large'
      : 'failed',
  message: toFailureMessage(error),
});

const within = <T>(
  promise: Promise<T>,
  { timeoutMs, signal, onTimeout, onAbort }: Bounds<T>
): Promise<T> =>
  new Promise<T>(resolve => {
    const abort = (): void => finish(onAbort);
    const timer = window.setTimeout(() => finish(onTimeout), timeoutMs);
    const finish = (value: T): void => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      resolve(value);
    };
    signal.addEventListener('abort', abort, { once: true });
    promise.then(finish);
  });

const isIdle = (): boolean =>
  state.capturedRuns.size === 0 && state.exportsInFlight === 0;

const notifyIfIdle = (): void => {
  if (isIdle()) {
    state.idleWaiters.splice(0).forEach(resolve => resolve());
  }
};

const whenIdle = (): Promise<void> =>
  isIdle()
    ? Promise.resolve()
    : new Promise<void>(resolve => {
        state.idleWaiters.push(resolve);
      });

/**
 * Hands a produced export to the active capture. Without one, it is dropped while an export left
 * running by a timed-out or aborted capture has not settled, for up to `timeoutMs` after the
 * capture ended, unless an export the user started is running, and saved via file-saver otherwise.
 */
export const deliverExport = (blob: Blob, fileName: string): void => {
  if (state.capture) {
    state.capture.settle({ kind: 'blob', blob });
    return;
  }
  if (isExportCapturing()) {
    return;
  }
  saveAs(blob, fileName);
};

/** Ends the active capture as failed instead of letting it wait for a blob that will not arrive. */
export const reportExportFailure = (error: unknown): void => {
  state.capture?.settle(toFailedOutcome(error));
};

/** True while exports are captured or dropped instead of downloaded, so no dialogs should open. */
export const isExportCapturing = (): boolean =>
  state.capture !== null ||
  (state.capturedRuns.size > 0 && state.exportsInFlight === 0);

export const trackViewExport = <T>(
  trackingFunction: ((event: T) => void) | undefined,
  event: T
): void => {
  if (!isExportCapturing()) {
    trackingFunction?.(event);
  }
};

/**
 * Registers an export as running, so a capture waits for it before it claims, and so it is saved,
 * tracked and asks for confirmations even while the export of a timed-out or aborted capture
 * keeps running. An export started while a capture is active belongs to the capture, which
 * already bounds how long it is waited for.
 */
export const exportInFlight =
  <Args extends unknown[], T>(
    run: (...args: Args) => Promise<T>
  ): ((...args: Args) => Promise<T>) =>
  (...args: Args): Promise<T> => {
    const start = (): Promise<T> =>
      new Promise<T>(resolve => resolve(run(...args)));
    if (state.capture !== null) {
      return start();
    }
    state.exportsInFlight += 1;
    return start().finally(() => {
      state.exportsInFlight -= 1;
      notifyIfIdle();
    });
  };

const startCapture = (
  run: () => Promise<void>,
  { timeoutMs, signal }: Omit<CaptureExportOptions, 'settleMs'>
): Promise<CaptureOutcome> => {
  const capture: Capture = { settle: noop };
  const claimed = new Promise<CaptureOutcome>(resolve => {
    capture.settle = resolve;
  });
  state.capture = capture;
  state.capturedRuns.add(capture);
  const release = (): void => {
    state.capturedRuns.delete(capture);
    notifyIfIdle();
  };

  const ran = Promise.resolve()
    .then(run)
    .then((): CaptureOutcome => ({ kind: 'no-delivery' }), toFailedOutcome);
  ran.then(outcome => {
    capture.settle(outcome);
    release();
  });

  return within(claimed, {
    timeoutMs,
    signal,
    onTimeout: { kind: 'timeout' },
    onAbort: { kind: 'aborted' },
  }).finally(() => {
    if (state.capture === capture) {
      state.capture = null;
    }
    const abandoned = window.setTimeout(release, timeoutMs);
    ran.then(() => window.clearTimeout(abandoned));
  });
};

/**
 * Runs an export and captures the first blob it delivers. It ends early with a reported failure
 * or rejection, with no delivery when the export settles without a blob, or on timeout or abort.
 * Exports already running, whether started by the user or left running by an earlier capture, are
 * waited for up to `settleMs` first, so their delivery is never taken for this one; the capture
 * ends busy when they do not settle.
 */
export const captureExport = async (
  run: () => Promise<void>,
  { timeoutMs, settleMs, signal }: CaptureExportOptions
): Promise<CaptureOutcome> => {
  if (signal.aborted) {
    return { kind: 'aborted' };
  }
  if (!isIdle()) {
    const previous = await within<CaptureOutcome | null>(
      whenIdle().then(() => null),
      {
        timeoutMs: settleMs,
        signal,
        onTimeout: { kind: 'busy' },
        onAbort: { kind: 'aborted' },
      }
    );
    if (previous) {
      return previous;
    }
  }
  return startCapture(run, { timeoutMs, signal });
};
